
import { useState } from "react";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, CreditCard } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { UserSubscriptionDetails } from "@/components/subscriptions/UserSubscriptionDetails";
import { usePlanSelectDialog } from "@/contexts/PlanSelectDialogContext";
import { UserSubscription } from "@/lib/types";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { useEffect } from "react";

export function SubscriptionsSection() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState<boolean>(true);
  const { getPublicBooleanSetting } = useSystemSettings();
  
  const { 
    useCurrentUserSubscriptionQuery
  } = useSubscriptions();
  
  const { 
    data: subscription, 
    isLoading: subscriptionLoading, 
    error: subscriptionError 
  } = useCurrentUserSubscriptionQuery();

  const { openDialog } = usePlanSelectDialog();

  // Check if subscriptions are enabled
  useEffect(() => {
    const checkSubscriptionsEnabled = async () => {
      try {
        const enabled = await getPublicBooleanSetting('subscriptions_enabled', true);
        setSubscriptionsEnabled(enabled);
      } catch (error) {
        console.error("Error checking if subscriptions are enabled:", error);
        setSubscriptionsEnabled(true);
      }
    };
    
    checkSubscriptionsEnabled();
  }, [getPublicBooleanSetting]);

  // If subscriptions are disabled, don't show this section
  if (!subscriptionsEnabled) {
    return null;
  }

  if (subscriptionLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <CreditCard className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Subscription</h2>
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <CreditCard className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Subscription</h2>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load subscription information. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <CreditCard className="h-5 w-5" />
        <h2 className="text-xl font-semibold">Subscription</h2>
      </div>
      
      <UserSubscriptionDetails 
        subscription={subscription as unknown as UserSubscription} 
        onViewPlans={openDialog} 
      />
    </div>
  );
}
